import { Outlet } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { PageTransition } from './motion/PageTransition';
import { LogOut, Shield } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import ThemeToggle from './ThemeToggle';
import { AdminGhostButton } from './admin';
import { useDesignTokens } from '../hooks/useTheme';
import Sidebar from './Sidebar';
import { ADMIN_NAV_CONFIG } from '../config/navigation';
import { ADMIN_ROUTES, adminUrl } from '../config/routes';
import { SidebarProvider, SIDEBAR_STORAGE_KEY_ADMIN } from '../context/SidebarContext';
import { PortalAccentContext } from '../context/portal-accent.context';

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const tokens = useDesignTokens();

  const handleLogout = async () => {
    await logout();
    window.location.href = adminUrl(ADMIN_ROUTES.LOGIN);
  };

  return (
    <PortalAccentContext.Provider value="amber">
      <SidebarProvider storageKey={SIDEBAR_STORAGE_KEY_ADMIN}>
        <div className="min-h-screen bg-zinc-100 dark:bg-zinc-900 select-none flex">
          {/* Modern Admin Sidebar */}
          <Sidebar
            navConfig={ADMIN_NAV_CONFIG}
            theme="amber"
            profileClickable={false}
            showSubscription={false}
            user={admin ? { email: admin.email, displayName: admin.full_name ?? null } : null}
          />

          {/* Main content area */}
          <div className="flex-1 flex flex-col">
            {/* Admin header */}
            <header className="bg-amber-600 dark:bg-amber-800 shrink-0">
              <div className="max-w-full px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                  <div className="flex items-center gap-2">
                    {/* Admin context badge - replaces static h1 */}
                    <Shield className="h-5 w-5 text-amber-100" />
                    <span className="text-sm font-medium text-amber-100">Admin Portal</span>
                  </div>

                  <div className={`flex items-center ${tokens.gaps.md}`}>
                    <span className="text-sm text-amber-100">{admin?.email}</span>
                    <ThemeToggle />
                    <AdminGhostButton
                      size="sm"
                      onClick={handleLogout}
                      icon={LogOut}
                      className="text-amber-100 hover:text-white"
                      title="Logout"
                    />
                  </div>
                </div>
              </div>
            </header>

            {/* Main content */}
            <main className={`flex-1 ${tokens.spacing.md} overflow-y-auto`}>
              <AnimatePresence mode="wait">
                <PageTransition>
                  <Outlet />
                </PageTransition>
              </AnimatePresence>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </PortalAccentContext.Provider>
  );
}
